import { type IDrawerListItem, WorkloadCreationDrawerEntities } from "@/models/workload-creation-drawer-model";
import { computeResourceService } from "@/services/control-plane/compute-resource.service/compute-resource.service";
import { environmentService } from "@/services/control-plane/environment.service/environment.service";
import { dataSourceService } from "@/services/control-plane/data-source.service/data-source.service";
import { workloadHybridUtil } from "@/utils/workload-hybrid.util/workload-hybrid.util";
import type { IAssetsFilter } from "@/models/filter.model";
import { type ISelectOption } from "@/models/global.model";
import { DATA_SOURCES_TYPES_TO_NAMES } from "@/models/data-source.model";
import { DATA_SOURCE_ICONS_NAMES } from "@/common/icons.constant";
import { AssetKind } from "@/swagger-models/assets-service-client";
import { useClusterStore } from "@/stores/cluster.store";
import { EWorkloadType } from "@/models/workload.model";
import { MIN_CLUSTER_VERSION_FOR_NFS_AND_HOST_PATH } from "@/common/version.constant";
import { inferenceWorkloadUtil } from "@/utils/inference-workload.util/inference-workload.util";
import { useSettingStore } from "@/stores/setting.store";

interface IDrawerEntityMeta {
  entityLabel: string;
  title: string;
  alternativeTitle?: () => string;
  defaultIcon: string;
  creationOptions?: ISelectOption[];
  loadFunction: (filter: IAssetsFilter, clusterId?: string) => Promise<IDrawerListItem[]>;
}

const loadDataSources = async (filter: IAssetsFilter, clusterId?: string) => {
  const dataSources = await dataSourceService.list({ ...filter, dataVolumes: useSettingStore().isDataVolumeEnabled });

  const loadedItems = workloadHybridUtil.dataSourceListToDrawerItems(dataSources);

  if (filter.complyToWorkloadType === EWorkloadType.Inference && clusterId) {
    loadedItems.forEach((item: IDrawerListItem) => {
      if (!item.kind) return;

      const supportsNfsAndHostPathForInference = useClusterStore().isClusterVersionSufficient(
        clusterId,
        MIN_CLUSTER_VERSION_FOR_NFS_AND_HOST_PATH,
      );

      const disabledInfo = inferenceWorkloadUtil.getDisabledInfoForDataSource(
        item.kind,
        supportsNfsAndHostPathForInference,
      );
      if (disabledInfo) {
        item.disabled = true;
        item.disabledInfo = disabledInfo;
      }
    });
  }
  return loadedItems;
};

const loadEnvironments = async (filter: IAssetsFilter) => {
  const environments = await environmentService.list(filter);
  return workloadHybridUtil.environmentListToDrawerItems(environments);
};

const loadComputeResources = async (filter: IAssetsFilter) => {
  const computeResources = await computeResourceService.list(filter);
  return workloadHybridUtil.computeListToDrawerItems(computeResources);
};

export const workloadCreationDrawerEntityMapper: Record<WorkloadCreationDrawerEntities, IDrawerEntityMeta> = {
  [WorkloadCreationDrawerEntities.Environment]: {
    entityLabel: "environment",
    title: "Environments",
    defaultIcon: "environment-gray-new",
    loadFunction: loadEnvironments,
  },
  [WorkloadCreationDrawerEntities.Compute]: {
    entityLabel: "compute resource",
    title: "Compute Resources",
    defaultIcon: "compute-resource-gray-new",
    loadFunction: loadComputeResources,
  },
  [WorkloadCreationDrawerEntities.DataSource]: {
    entityLabel: "setup",
    title: "Data Sources",
    alternativeTitle: () => (useSettingStore().isDataVolumeEnabled ? "Data & storage" : ""),
    defaultIcon: "data-source-gray-new",
    creationOptions: [
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.NFS,
        value: AssetKind.Nfs,
        svg: DATA_SOURCE_ICONS_NAMES.nfs,
        aid: `ds-${AssetKind.Nfs}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.PVC,
        value: AssetKind.Pvc,
        svg: DATA_SOURCE_ICONS_NAMES.pvc,
        aid: `ds-${AssetKind.Pvc}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.S3,
        value: AssetKind.S3,
        svg: DATA_SOURCE_ICONS_NAMES.s3,
        aid: `ds-${AssetKind.S3}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.GIT,
        value: AssetKind.Git,
        svg: DATA_SOURCE_ICONS_NAMES.git,
        aid: `ds-${AssetKind.Git}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.HOST_PATH,
        value: AssetKind.HostPath,
        svg: DATA_SOURCE_ICONS_NAMES.hostPath,
        aid: `ds-${AssetKind.HostPath}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.CONFIG_MAP,
        value: AssetKind.ConfigMap,
        svg: DATA_SOURCE_ICONS_NAMES["config-map"],
        aid: `ds-${AssetKind.ConfigMap}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.SECRET_VOLUME,
        value: AssetKind.SecretVolume,
        svg: DATA_SOURCE_ICONS_NAMES["secret-volume"],
        aid: `ds-${AssetKind.SecretVolume}-option`,
      },
      {
        label: DATA_SOURCES_TYPES_TO_NAMES.DATA_VOLUME,
        value: AssetKind.DataVolume,
        svg: DATA_SOURCE_ICONS_NAMES["data-volume"],
        aid: `ds-${AssetKind.DataVolume}-option`,
      },
    ],
    loadFunction: loadDataSources,
  },
};
